/* =====================================================================
   MK PDP · Produktseite
   Nur was CSS/Liquid nicht können:
   1  Mobile-Sheet ziehen (Peek / Halb / Ganz) an den Stack-Grenzen
   2  Tabs: Sheet öffnen, Desktop-Höhe, Verlauf
   3  Galerie: Thumbnails, Snap, Zoom
   4  Kleinigkeiten: Header sichtbar, Laufschrift, Editor-Hinweis
   Inhalte, Reihenfolge und Texte kommen aus den Blöcken im Theme-Editor.
   ===================================================================== */
(() => {
  if (window.__mkPdp) return; // Datei nur einmal aktiv, auch wenn der Editor die Section neu lädt
  window.__mkPdp = true;
  const root = document.documentElement;
  const mqDesk = matchMedia('(min-width: 1000px)');
  const isDesk = () => mqDesk.matches;
  const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  const instances = new Map();

  function init(page) {
    if (!page || page.dataset.mkReady) return;
    page.dataset.mkReady = '1';

    let disposed = false;
    const removers = [], observers = [], timers = new Set(), frames = new Set();
    const listen = (target, type, handler, options) => {
      target.addEventListener(type, handler, options);
      removers.push(() => target.removeEventListener(type, handler, options));
    };
    const observe = (observer) => { observers.push(observer); return observer; };
    const later = (callback, delay) => {
      const id = setTimeout(() => { timers.delete(id); if (!disposed) callback(); }, delay);
      timers.add(id);
      return id;
    };
    const frame = (callback) => {
      const id = requestAnimationFrame((time) => { frames.delete(id); if (!disposed) callback(time); });
      frames.add(id);
      return id;
    };
    instances.set(page, () => {
      disposed = true;
      removers.forEach((remove) => remove());
      observers.forEach((observer) => observer.disconnect());
      timers.forEach(clearTimeout);
      frames.forEach(cancelAnimationFrame);
      delete page.dataset.mkReady;
      delete page.dataset.mkProductAway;
      const sheet = page.querySelector('safe-sticky.product-info');
      if (sheet) {
        sheet.inert = false;
        sheet.style.removeProperty('--mk-sheet-y');
      }
      instances.delete(page);
      if (!instances.size) {
        document.querySelector('.mk-scrim')?.remove();
        delete root.dataset.mkSheet;
        root.classList.remove('mk-dragging', 'mk-snap');
        ['--mk-sheet-y', '--mk-peek'].forEach((name) => root.style.removeProperty(name));
      }
    });

    const $ = (sel) => page.querySelector(sel);
    const info = () => $('safe-sticky.product-info');
    const list = () => $('.product-info__block-list');
    const stack = (n) => $(`.mk-stack[data-stack="${n}"]`);

    /* ---------- 4 · Header auf der Produktseite immer sichtbar ---------- */
    const header = document.querySelector('.shopify-section--header x-header');
    if (header) {
      const keep = () => {
        if (header.hasAttribute('hide-on-scroll')) header.removeAttribute('hide-on-scroll');
        if (header.classList.contains('is-hidden')) header.classList.remove('is-hidden');
      };
      keep();
      observe(new MutationObserver(keep)).observe(header, { attributes: true, attributeFilter: ['class', 'hide-on-scroll'] });
    }

    /* ---------- 1 · Mobile-Sheet ---------- */
    const order = ['collapsed', 'peek', 'half', 'full'];
    let state = 'peek';
    let scrim = document.querySelector('.mk-scrim');
    if (!scrim) {
      scrim = document.createElement('div');
      scrim.className = 'mk-scrim';
      document.body.appendChild(scrim);
    }

    const restoreHandle = document.createElement('button');
    restoreHandle.type = 'button';
    restoreHandle.className = 'mk-sheet-restore';
    restoreHandle.setAttribute('aria-label', document.documentElement.lang.startsWith('de')
      ? 'Produktinformationen öffnen' : 'Open product information');
    restoreHandle.tabIndex = -1;
    info()?.appendChild(restoreHandle);
    listen(restoreHandle, 'click', () => setSheet('peek'));
    removers.push(() => {
      restoreHandle.remove();
      if (list()) list().inert = false;
    });

    // sichtbare Höhe einer Stufe = Unterkante von Stack 1 bzw. 2 (+ etwas Luft)
    function visible(s) {
      const el = info();
      if (!el) return 0;
      if (s === 'collapsed') return restoreHandle.offsetHeight || 28;
      if (s === 'full') return el.offsetHeight;
      const st = stack(s === 'half' ? 2 : 1);
      if (!st) return el.offsetHeight;
      const l = list();
      const bottom = st.getBoundingClientRect().bottom - el.getBoundingClientRect().top + (l ? l.scrollTop : 0);
      return Math.min(el.offsetHeight, bottom + 10);
    }
    const offsetFor = (s) => Math.max(0, (info()?.offsetHeight || 0) - visible(s));

    function setSheet(s, animate = true) {
      if (isDesk()) {
        restoreHandle.tabIndex = -1;
        if (list()) list().inert = false;
        delete root.dataset.mkSheet;
        info()?.style.removeProperty('--mk-sheet-y');
        root.style.removeProperty('--mk-peek');
        return;
      }
      const peek = Math.round(visible('peek'));
      warnStack1(peek);
      if (peek < 60) return; // Prestige baut gerade neu auf (oder Stack 1 ist leer)
      const sheetOffset = Math.round(offsetFor(s));
      state = s;
      root.dataset.mkSheet = s;
      restoreHandle.tabIndex = s === 'collapsed' ? 0 : -1;
      if (list()) list().inert = s === 'collapsed';
      if (!animate) root.classList.add('mk-measuring');
      info()?.style.setProperty('--mk-sheet-y', sheetOffset + 'px');
      root.style.setProperty('--mk-peek', peek + 'px');
      if (!animate) {
        frame(() => frame(() => {
          root.classList.remove('mk-measuring');
        }));
      }
      if (s !== 'full' && animate && list()) list().scrollTop = 0;
      page.querySelectorAll('.mk-more').forEach((b) => b.setAttribute('aria-expanded', s === 'full'));
      if (s === 'full') {
        try { sessionStorage.setItem('mk_full_seen', '1'); } catch (e) {}
        page.querySelectorAll('.mk-more.is-nudge').forEach((b) => b.classList.remove('is-nudge'));
      }
      clearTimeout(fadeTimer);
      timers.delete(fadeTimer);

      fadeTimer = later(() => {
        fadeTimer = 0;
        fade();
      }, 600);
    }

    // Gestensteuerung: Grenzen einmal beim Anfassen messen.
    let y0 = 0;
    let startY = 0;
    let dragMax = 0;
    let drag = false;
    let moved = false;
    let grab = false;

    listen(document, 'pointerdown', (e) => {
      const el = info();
      if (isDesk() || !el || !el.contains(e.target)) return;

      const fromTop = e.clientY - el.getBoundingClientRect().top;

      if (
        state === 'full' &&
        e.pointerType === 'touch' &&
        fromTop >= 18
      ) return;

      const head =
        fromTop < 18 ||
        (
          state !== 'full' &&
          !e.target.closest(
            'a,button,input,label,select,textarea,klarna-placement'
          )
        );

      const top =
        state === 'full' &&
        list() &&
        list().scrollTop <= 1 &&
        !e.target.closest('a,button,input,label,select,textarea');

      if (!head && !top) return;

      grab = fromTop < 18;
      y0 = e.clientY;
      startY = offsetFor(state);
      dragMax = offsetFor('collapsed');
      drag = true;
      moved = false;
    }, { passive: true });

    listen(document, 'pointermove', (e) => {
      if (!drag) return;

      const dy = e.clientY - y0;

      if (!moved && Math.abs(dy) < 6) return;

      if (state === 'full' && dy < 0 && !grab) {
        drag = false;
        return;
      }

      if (!moved) root.classList.add('mk-dragging');
      moved = true;

      info()?.style.setProperty(
        '--mk-sheet-y',
        Math.min(dragMax, Math.max(0, startY + dy)) + 'px'
      );
    }, { passive: true });

    const end = (e) => {
      if (!drag) return;

      drag = false;
      root.classList.remove('mk-dragging');

      if (!moved) {
        if (grab) {
          setSheet(
            state === 'full'
              ? 'peek'
              : order[order.indexOf(state) + 1]
          );
        }
        return;
      }

      const sheetY = parseFloat(
        info()?.style.getPropertyValue('--mk-sheet-y')
      );
      const cur = Number.isFinite(sheetY) ? sheetY : startY;
      const dy = e.clientY - y0;

      let best = order
        .map((s) => [s, Math.abs(offsetFor(s) - cur)])
        .sort((a, b) => a[1] - b[1])[0][0];

      const i = order.indexOf(state);

      if (Math.abs(dy) > 40 && best === state) {
        best = order[
          Math.min(
            order.length - 1,
            Math.max(0, i + (dy < 0 ? 1 : -1))
          )
        ];
      }

      setSheet(best);
    };

    // Geöffnete Karte: zuerst Inhalt scrollen.
    // Nur eine neue Abwärtsbewegung am Textanfang zieht die Karte.
    let touchGesture = null;
    let inertiaFrame = 0;

    const stopInertia = () => {
      cancelAnimationFrame(inertiaFrame);
      frames.delete(inertiaFrame);
      inertiaFrame = 0;
    };

    function coast(l, velocity) {
      stopInertia();
      if (Math.abs(velocity) < 0.05) return;

      const maximum = Math.max(0, l.scrollHeight - l.clientHeight);
      let previous = performance.now();

      const tick = (now) => {
        if (
          disposed ||
          state !== 'full' ||
          !page.isConnected
        ) return;

        const dt = Math.min(32, Math.max(1, now - previous));
        previous = now;

        velocity *= Math.exp(-dt / 180);

        const before = l.scrollTop;
        l.scrollTop = Math.max(
          0,
          Math.min(maximum, before + velocity * dt)
        );

        if (
          Math.abs(velocity) > 0.025 &&
          Math.abs(l.scrollTop - before) > 0.1
        ) {
          inertiaFrame = frame(tick);
        }
      };

      inertiaFrame = frame(tick);
    }

    listen(page, 'touchstart', (e) => {
      stopInertia();
      touchGesture = null;

      if (
        isDesk() ||
        state !== 'full' ||
        e.touches.length !== 1
      ) return;

      const l = list();
      const el = info();
      const t = e.touches[0];

      if (!l || !el || !l.contains(e.target)) return;
      if (t.clientY - el.getBoundingClientRect().top < 18) return;

      if (
        e.target.closest('input,select,textarea,klarna-placement')
      ) return;

      touchGesture = {
        l,
        y: t.clientY,
        x: t.clientX,
        startTop: l.scrollTop,
        maximum: Math.max(0, l.scrollHeight - l.clientHeight),
        mayPull:
          l.scrollTop <= 1 &&
          !e.target.closest('a,button,label'),
        mode: null,
        lastY: t.clientY,
        lastTime: performance.now(),
        velocity: 0
      };
    }, { passive: true });

    listen(page, 'touchmove', (e) => {
      const g = touchGesture;
      if (!g || e.touches.length !== 1) return;

      const t = e.touches[0];
      const dy = t.clientY - g.y;
      const dx = t.clientX - g.x;

      if (!g.mode) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;

        if (Math.abs(dx) > Math.abs(dy)) {
          touchGesture = null;
          return;
        }

        g.mode = dy > 0 && g.mayPull ? 'sheet' : 'scroll';

        if (g.mode === 'sheet') {
          y0 = g.y;
          startY = offsetFor(state);
          dragMax = offsetFor('collapsed');
          root.classList.add('mk-dragging');
        }
      }

      if (e.cancelable) e.preventDefault();

      if (g.mode === 'sheet') {
        drag = true;
        moved = true;
        grab = false;

        info()?.style.setProperty(
          '--mk-sheet-y',
          Math.min(dragMax, Math.max(0, startY + dy)) + 'px'
        );
      } else {
        drag = false;

        g.l.scrollTop = Math.max(
          0,
          Math.min(g.maximum, g.startTop - dy)
        );

        const now = performance.now();
        const dt = Math.max(1, now - g.lastTime);

        g.velocity = Math.max(
          -2.5,
          Math.min(2.5, (g.lastY - t.clientY) / dt)
        );

        g.lastY = t.clientY;
        g.lastTime = now;
      }
    }, { passive: false });

    listen(page, 'touchend', (e) => {
      const g = touchGesture;
      touchGesture = null;

      if (!g) return;

      if (g.mode === 'sheet' && e.changedTouches.length) {
        end({ clientY: e.changedTouches[0].clientY });
      } else if (
        g.mode === 'scroll' &&
        performance.now() - g.lastTime < 80
      ) {
        coast(g.l, g.velocity);
      }
    });

    const cancelDrag = () => {
      if (!drag) return;

      drag = false;
      root.classList.remove('mk-dragging');
      setSheet(state, false);
    };

    listen(page, 'touchcancel', () => {
      touchGesture = null;
      stopInertia();
      cancelDrag();
    });

    listen(document, 'pointerup', (e) => {
      if (e.pointerType === 'touch' && touchGesture) return;
      end(e);
    });

    listen(document, 'pointercancel', (e) => {
      if (e.pointerType === 'touch' && touchGesture) return;
      cancelDrag();
    });

    listen(scrim, 'click', () => setSheet('peek'));

    // Nach „In den Warenkorb“ zurück auf Peek
    listen(document, 'cart:change', () => { if (!isDesk()) setSheet('peek'); });

    /* ---------- 2 · Tabs ---------- */
    // Hinweis-Zeile „Description · Details · Shipping“ öffnet ganz; Pfeil wippt nur beim ersten Mal
    let seen = false;
    try { seen = !!sessionStorage.getItem('mk_full_seen'); } catch (e) {}
    page.querySelectorAll('.mk-more').forEach((b) => b.classList.toggle('is-nudge', !seen));
    listen(page, 'click', (e) => {
      if (e.target.closest('[data-mk-open="full"]')) setSheet('full');
    });
    // Tastatur / VoiceOver: Fokus unterhalb von Stack 1 öffnet das Sheet, Escape schließt
    listen(page, 'focusin', (e) => {
      if (!isDesk() && state === 'peek' && info()?.contains(e.target) && !stack(1)?.contains(e.target)) setSheet('full');
    });
    listen(page, 'keydown', (e) => {
      if (e.key === 'Escape' && !isDesk() && state !== 'peek') setSheet('peek');
    });

    listen(page, 'change', (e) => {
      if (!e.target.matches('.mk-tab__radio')) return;
      if (!isDesk() && state !== 'full') setSheet('full');
      frame(fitPanel);
    });

    let fadeFrame = 0;
    let fadeTimer = 0;

    const hasMore = (el) => el.scrollHeight - el.scrollTop - el.clientHeight > 8;

    function fade() {
      if (fadeFrame || disposed) return;

      fadeFrame = frame(() => {
        fadeFrame = 0;

        const l = list();
        if (l) l.classList.toggle('mk-fade-b', !isDesk() && state === 'full' && hasMore(l));

        page.querySelectorAll('.mk-tab__panel').forEach((p) => {
          p.classList.toggle('mk-fade-b', isDesk() && hasMore(p));
        });
      });
    }
    listen(page, 'scroll', fade, { capture: true, passive: true });

    // Desktop: offener Tab bekommt genau den Platz bis zum Bildschirmrand – die Spalte selbst bewegt sich nie
    function fitPanel() {
      const sec = page.closest('.shopify-section') || page;
      const el = info();

      if (!isDesk()) {
        el?.classList.remove('mk-info-scroll');
        sec.style.removeProperty('--mk-pmax');
        return fade();
      }

      const panel = $(
        '.mk-tab__radio:checked + .mk-tab__label + .mk-tab__panel'
      );
      if (!el || !panel) return fade();

      const top = parseFloat(getComputedStyle(el).top) || 86;
      const panelOffset = panel.getBoundingClientRect().top
        - el.getBoundingClientRect().top + el.scrollTop;

      const legal = $('.mk-legal');
      const below = legal ? legal.offsetHeight + 40 : 24;
      const available = innerHeight - top - panelOffset - below;

      const wasScrollable = el.classList.contains('mk-info-scroll');
      const scrollable = available < (wasScrollable ? 240 : 200);

      el.classList.toggle('mk-info-scroll', scrollable);

      if (!scrollable && wasScrollable) {
        el.scrollTop = 0;
      }

      sec.style.setProperty(
        '--mk-pmax',
        Math.max(80, available) + 'px'
      );

      fade();
    }

    /* ---------- 3 · Galerie ---------- */
    const thumbs = $('.mk-thumbs');
    const goTo = (k) => $('#mk-shot-' + k)?.scrollIntoView({ behavior: isDesk() || reduce() ? 'instant' : 'smooth', block: 'start' });
    if (thumbs) {
      listen(thumbs, 'click', (e) => {
        const b = e.target.closest('button');
        if (b) goTo(b.dataset.k);
      });
      listen(thumbs, 'keydown', (e) => {
        if (!/Arrow(Up|Down)/.test(e.key)) return;
        e.preventDefault();
        const bs = [...thumbs.querySelectorAll('button')];
        const j = Math.max(0, Math.min(bs.length - 1, bs.indexOf(document.activeElement) + (e.key === 'ArrowDown' ? 1 : -1)));
        bs[j].focus({ preventScroll: true });
        bs[j].click();
      });
      const io = observe(new IntersectionObserver((entries) => entries.forEach((en) => {
        if (!en.isIntersecting) return;
        thumbs.querySelectorAll('button').forEach((b) => {
          const on = b.dataset.k === en.target.dataset.k;
          b.setAttribute('aria-current', on);
          b.tabIndex = on ? 0 : -1;
          if (on && isDesk()) {
            const top = b.getBoundingClientRect().top - thumbs.getBoundingClientRect().top + thumbs.scrollTop;
            if (top < thumbs.scrollTop) thumbs.scrollTop = top;
            else if (top + b.offsetHeight > thumbs.scrollTop + thumbs.clientHeight) {
              thumbs.scrollTop = top + b.offsetHeight - thumbs.clientHeight;
            }
          }
        });
      }), { threshold: 0.6 }));
      page.querySelectorAll('.mk-shot').forEach((s) => io.observe(s));
    }

    // Produktkarte nach der Galerie ausblenden und bei Rückkehr wieder anzeigen.
    const shots = $('.mk-shots');
    let boundaryFrame = 0, productAway = false;
    function syncGalleryBoundary() {
      if (disposed || !page.isConnected || !shots) return;
      const bounds = shots.getBoundingClientRect();
      const viewport = innerHeight;
      // The next section enters the visible area above the closed sheet.
      const peek = isDesk() ? 0 : visible('peek');
      const away = !isDesk() && bounds.bottom < viewport - peek + (productAway ? 4 : -4);
      if (away === productAway) return;
      productAway = away;
      if (away) {
        drag = false;
        root.classList.remove('mk-dragging');
        setSheet(state, false);
        page.dataset.mkProductAway = '';
      } else {
        delete page.dataset.mkProductAway;
      }
      const el = info();
      if (el) el.inert = away;
    }
    function scheduleBoundary() {
      if (boundaryFrame || disposed) return;
      boundaryFrame = frame(() => { boundaryFrame = 0; syncGalleryBoundary(); });
    }
    listen(window, 'scroll', scheduleBoundary, { passive: true });
    if (shots) observe(new ResizeObserver(scheduleBoundary)).observe(shots);
    syncGalleryBoundary();

    // Zoom: Klick aufs Bild öffnet die Prestige-Lightbox an derselben Stelle
    listen(page, 'click', (e) => {
      const shot = e.target.closest('.mk-shot[data-zoom]');
      if (!shot || e.target.closest('video,button')) return;
      $('open-lightbox-button')?.dispatchEvent(new CustomEvent('lightbox:open', { bubbles: true, detail: { index: parseInt(shot.dataset.zoom, 10) } }));
    });

    /* ---------- 4 · Laufschrift für Titel + Model-Zeile (Mobile) ---------- */
    function marquee(el) {
      if (!el || isDesk() || reduce()) return;
      if (!el.querySelector(':scope > .mk-mq-in')) {
        const span = document.createElement('span');
        span.className = 'mk-mq-in';
        while (el.firstChild) span.appendChild(el.firstChild);
        el.appendChild(span);
        el.classList.add('mk-mq');
        el.addEventListener('click', () => marquee(el));
      }
      const d = el.firstElementChild.scrollWidth - el.clientWidth;
      el.classList.toggle('is-over', d > 2);
      el.classList.remove('run');
      if (d <= 2) return;
      el.style.setProperty('--mq', -(d + 6) + 'px');
      el.style.setProperty('--mqd', Math.max(5, 3 + d / 25) + 's');
      void el.offsetWidth;
      el.classList.add('run');
    }
    later(() => { marquee($('.product-title')); marquee($('.mk-model-size')); }, 1200);

    /* ---------- 4 · Editor: Warnung, wenn Stack 1 höher als 240px ---------- */
    function warnStack1(h) {
      const s1 = stack(1);
      if (!s1 || !window.Shopify?.designMode) return;
      if (h > 240 || h < 60) s1.dataset.mkWarn = `Stack 1: ${h}px – Ziel 60–240px. Stack-Trenner prüfen (nur im Editor sichtbar)`;
      else delete s1.dataset.mkWarn;
    }

    /* ---------- Neu messen, wenn sich etwas ändert ---------- */
    let raf = 0;
    let refreshPending = false;

    const refresh = () => {
      if (disposed || !page.isConnected) return;

      cancelAnimationFrame(raf);
      frames.delete(raf);
      raf = 0;

      if (drag || touchGesture) {
        refreshPending = true;
        return;
      }

      raf = frame(() => {
        raf = 0;
        if (drag || touchGesture) {
          refreshPending = true;
          return;
        }

        refreshPending = false;
        setSheet(state, false);
        fitPanel();
        syncGalleryBoundary();
      });
    };

    const finishRefresh = () => {
      if (refreshPending) refresh();
    };

    listen(document, 'pointerup', finishRefresh);
    listen(document, 'pointercancel', finishRefresh);
    listen(document, 'touchend', finishRefresh);
    listen(document, 'touchcancel', finishRefresh);
    const ro = observe(new ResizeObserver(refresh));
    const watch = () => {
      const model = $('[data-block-type="variant-picker"] .mk-model-size') || $('.mk-model-size');
      const guide = $('#sizefox-container')?.closest('.product-info__block-item');
      if (model && guide && guide.parentElement === stack(1)) {
        page.querySelectorAll('.mk-model-size').forEach((el) => {
          if (el !== model) el.remove();
        });
        if (guide.nextElementSibling !== model) guide.after(model);
      }
      ro.disconnect();
      [stack(1), stack(2), info()].forEach((el) => el && ro.observe(el));
    };
    watch();
    listen(document, 'variant:change', () => { watch(); refresh(); });
    listen(mqDesk, 'change', refresh);
    let viewportWidth = document.documentElement.clientWidth;
    listen(window, 'resize', () => {
      const nextWidth = document.documentElement.clientWidth;
      const widthChanged = nextWidth !== viewportWidth;
      viewportWidth = nextWidth;
      // Mobile Browserleisten ändern nur die Höhe: Kartenstufe nicht neu messen.
      if (!isDesk() && !widthChanged) return;
      refresh();
    });
    document.fonts?.ready.then(refresh);
    listen(window, 'load', refresh);
    refresh();
  }

  // Prestige fokussiert nach dem Größenwechsel neu → im Mobile-Sheet ohne Springen
  const nativeFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function (opts) {
    if (!isDesk() && this.closest?.('.mk-pdp safe-sticky.product-info')) return nativeFocus.call(this, { ...(opts || {}), preventScroll: true });
    return nativeFocus.call(this, opts);
  };

  const start = () => {
    for (const [page, dispose] of instances) if (!page.isConnected) dispose();
    document.querySelectorAll('.mk-pdp').forEach(init);
  };
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [page, dispose] of instances) {
      if (event.target === page || event.target.contains(page)) dispose();
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  // Theme-Editor: Section wird neu geladen → neu starten
  document.addEventListener('shopify:section:load', start);
  // Theme-Editor: Tab-Block anklicken → dieser Tab ist offen
  document.addEventListener('shopify:block:select', (e) => {
    const r = document.getElementById('mk-tab-' + e.detail.blockId);
    if (r && !r.checked) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); }
  });
})();
